/*
 * 작성자 : 김광민
 * 날짜 : 2023.08.09
 * 기능 : 메인메뉴 리스트 출력 컴포넌트
 */

import React, { useEffect, useState, Dispatch, SetStateAction, useLayoutEffect, useRef } from 'react';
import { useQuery, useQueries } from '@tanstack/react-query';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { createApprovalAlertState } from '../hooks/useApprovalAlertEntry';
import { useRecoilState, useSetRecoilState, useRecoilValue } from 'recoil';
import styled from 'styled-components';

import { ISideMenuObject } from 'customTypes';
import { msiteListState, msiteUseYnState, themeState, userState, pageInfoState, IPageInfo, asideState } from '../atoms';
import Portal from './Portal';
import SearchSelectBoxSm from './SearchSelectBoxSm';
import UserInfoSettingModal from './Modal/UserInfoSettingModal';
import * as cctv from '../utils/cctv';
import { useTranslation } from 'react-i18next';
import { apiGet } from '../services/_common';
import { useThemeLogo } from '../services/getInitialColorMode';
import { getMSiteList } from '../hooks/useMSite';
import BroadcastLiveModal from './Modal/BroadcastLiveModal';
import { toast } from 'react-toastify';
import axios from 'axios';
import { logPost } from '../services/log';
import { isMobile } from '../utils/checkMobile';

interface AsideMainMenuProps {
  foldMenu: boolean;
  foldingMenu: (e: React.MouseEvent<HTMLElement>) => void;
  sideMenuList: ISideMenuObject[];
  onClickAsideList: (el: any) => void;
  mainSelected: string;
  mainFocus: string;
  closeAside: () => void;
  openMenu: boolean;
  useBroadcast: { isActiveMenu: boolean; menu: any; pageMoveFunction: () => void };
  W: number;
  hasVisitorMenuAuth: boolean;
  subMenu: any;
  pageMovement: (sub: any) => void;
  setLinkPath: (mtCd: string, mdCd: string) => string;
  subSelected: string;
  setSubSelected: Dispatch<SetStateAction<string>>;
  setMainFocus: Dispatch<SetStateAction<string>>;
}

const Root = styled.div`
  display: flex;
  &.hideRoot {
    visibility: hidden;
    position: absolute;
  }
  .aside {
    height: 100%;
    min-height: 100%;
    position: relative;
    z-index: 5001;
    display: flex;
    flex-direction: column;
    background-color: var(--board);
    overflow: visible;
  }
  .aside-header {
    display: flex;
    align-items: center;
    justify-content: space-around;
    width: 100%;
    min-height: 88px;
    padding: 1rem 0.75rem 0.5rem;

    .close-menu {
      position: fixed;
      top: 1rem;
      right: 1rem;
      display: flex;
      color: var(--text-secondary);
      background-color: var(--tonal-deep);
      width: 2rem;
      height: 2rem;
      user-select: none;
      cursor: pointer;
      align-items: center;
      justify-content: center;
      border-radius: 50%;
    }
    @media (min-width: 1025px) {
      .close-menu {
        display: none;
      }
    }

    .aside-info {
      display: flex;
      align-items: center;
      justify-content: space-between;
      width: 100%;

      > a {
        display: flex;
        width: 100%;
        align-items: center;
        gap: 0.5rem;
        cursor: pointer;

        > figure {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 4rem;
          height: 4rem;

          > img {
            display: block;
            width: 100%;
            height: 100%;
            object-fit: contain;
            padding: 0.5rem;
            border-radius: 0.5rem;
            box-shadow: 1px 1px 6px 0 var(--op-bg-15);
          }
        }

        > h1 {
          font-size: 1.25rem;
          color: var(--text-primary);
          line-height: 1.4;
          display: -webkit-box;
          -webkit-box-orient: vertical;
          -webkit-line-clamp: 2;
          overflow: hidden;
          text-overflow: ellipsis;
          flex: 1;
        }
      }

      .icon-close {
        display: flex;
        justify-content: center;
        align-items: center;
        width: 100%;
        height: 3rem;
        border-radius: 0.5rem;
        background-color: var(--tonal);
        color: var(--text-tertiary);
        cursor: pointer;

        &:hover {
          background-color: var(--tonal-deep);
        }
      }

      .side-right {
        display: flex;
        align-items: center;
        gap: 0.25rem;

        .side-right-icon {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 1.5rem;
          height: 1.5rem;
          border-radius: 50%;
          cursor: pointer;

          > span {
            font-size: 18px;
            color: var(--text-primary);
          }

          &.icon-select {
            &:hover {
              background-color: var(--tonal-deep);
            }
          }

          &.icon-open {
            border: 1px solid var(--outline);

            &:hover {
              border-color: var(--outline-em);
            }

            @media screen and (max-width: 1024px) {
              & {
                display: none;
              }
            }
          }
        }
      }
    }
  }
  .aside-top {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0 1.5rem;
    margin-top: 1.5rem;
    @media (min-width: 1025px) {
      padding: 0 0.75rem;
      margin: 0.5rem 0;
    }
    .icon-menu {
      user-select: none;
      cursor: pointer;
      width: 3.5rem;
      height: 3rem;
      align-items: center;
      justify-content: center;
      border-radius: 1rem;
      /* border-radius: 0.25rem; */
      span.rotate180 {
        transform: rotate(180deg);
      }
      @media (min-width: 1025px) {
        :hover {
          background-color: var(--tonal-deep);
        }
      }
    }
    .hamburger {
      display: none;
    }
    .close-menu {
      position: fixed;
      top: 1rem;
      right: 1rem;
      display: flex;
      color: var(--text-secondary);
      background-color: var(--tonal-deep);
      width: 2rem;
      height: 2rem;
    }
    @media (min-width: 1025px) {
      .hamburger {
        display: flex;
        color: var(--text-primary);
      }
      .close-menu {
        display: none;
      }
    }
    .logo {
      flex-grow: 1;
      height: 3rem;
      /* height: 3.5rem; */
      @media (min-width: 640px) {
        height: 2.5rem;
      }
      cursor: pointer;
      > img {
        height: inherit;
        user-select: none;
      }
    }
  }
  .aside-top {
    align-items: flex-end;
  }
  .aside-fold {
    width: 5rem;
  }
  .aside-default {
    width: 100vw;
    width: 20rem;
    width: 17.5rem;
    @media (min-width: 640px) {
      width: 16rem;
    }
  }
  .aside-icon {
    height: 4rem;
    display: flex;
    justify-content: flex-end;
    > svg {
      height: 70%;
    }
  }

  /* 접힘 상태에서 하위메뉴를 사이드바 밖에 표시하는 플라이아웃 */
  .fold-submenu {
    position: fixed;
    z-index: 6000;
    min-width: 12rem;
    max-width: 18rem;
    background-color: var(--board);
    border: 1px solid var(--outline);
    border-radius: 0.5rem;
    box-shadow: 0 12px 28px var(--alpha-17);
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }
  .fold-submenu-title {
    padding: 0.5rem 0.75rem;
    font-size: 0.875rem;
    font-weight: var(--font-semibold);
    color: var(--text-primary);
    border-bottom: 1px solid var(--outline);
    background-color: var(--tonal);
  }
  .fold-submenu-list {
    display: flex;
    flex-direction: column;
    overflow-y: auto;
    overflow-x: hidden;
    max-height: calc(100% - 2.5rem);
    flex: 1;
    min-height: 0;
  }
  .fold-submenu-list a {
    display: flex;
    align-items: center;
    padding: 0.375rem 0.75rem;
    text-decoration: none;
    color: var(--text-secondary);
    font-size: 0.875rem;
    white-space: nowrap;
  }
  .fold-submenu-list a:hover {
    background-color: var(--tonal-deep);
    color: var(--text-primary);
  }
  .fold-submenu-list a.active {
    background-color: var(--selected-primary);
    color: #fff;
  }
  .aside-container {
    width: 100%;
    height: 100%;
    padding: 0 0.75rem 0.5rem 0.75rem;
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    overflow-y: auto;
    overflow-x: hidden;
    scrollbar-gutter: stable;
  }
  .aside-container.fold {
    overflow-y: scroll;
    overflow-x: hidden;
  }
  .aside-container.fixed {
    height: fit-content;
    min-height: fit-content;
    padding: 0.5rem 0.75rem;
    overflow: hidden;
  }
  .menu-item-wrapper {
    display: flex;
    flex-direction: column;
    position: relative;
    overflow: visible;
  }
  .submenu-accordion {
    display: flex;
    flex-direction: column;
    overflow: hidden;
    max-height: 0;
    transition: max-height 0.3s ease;
    &.open {
      max-height: 1000px;
    }
    .submenu-list {
      padding: 0.25rem 0;
      display: flex;
      flex-direction: column;
      gap: 0.25rem;
      .sub-list {
        user-select: none;
        cursor: pointer;
        display: flex;
        align-items: center;
        padding: 0 0.25rem 0 3.5rem;
        border-radius: 0.25rem;
        height: 2.5rem;
        flex-shrink: 0;
        .sub-title {
          color: var(--text-secondary);
          font-weight: var(--font-semibold);
          font-size: 0.925rem;
          line-height: 1.4;
        }
        @media (min-width: 1025px) {
          :hover {
            .sub-title {
              color: var(--text-primary);
            }
          }
        }
      }
      .sub-list.active {
        .sub-title {
          color: var(--selected-primary);
          font-weight: var(--font-semibold);
        }
        :hover {
          .sub-title {
            color: var(--selected-primary);
          }
        }
      }
    }
    &.fold {
      &::-webkit-scrollbar {
        width: 0;
        height: 0;
      }
      &::-webkit-scrollbar-track {
        width: 0;
      }
    }

    &.fixed {
      height: 4rem !important;
      padding: 0 0.75rem;
      overflow: hidden;
      .main-list {
        background-color: var(--tonal-blue);
      }
      .alarm-stop-list {
        background-color: var(--tonal-red);
      }
      .main-title {
        font-weight: 600;
      }
    }
  }
  .aside-bottom {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0.75rem;
    border-top: 1px solid var(--outline);
    overflow: visible;
    .language-select {
      width: 4rem;
    }
    .user-info {
      order: 1;
    }
    .notification-wrapper {
      order: 2;
      position: relative;
      display: flex;
      align-items: center;
      justify-content: center;
      overflow: visible;
      .icon-menu {
        position: relative;
        cursor: pointer;
      }
      .badge {
        position: absolute;
        top: 0.2rem;
        right: 0.2rem;
        width: 1.1rem;
        height: 1.1rem;
        padding: 0;
        border-radius: 50%;
        background: var(--filled-red);
        color: #fff;
        font-size: 0.7rem;
        display: flex;
        align-items: center;
        justify-content: center;
        line-height: 1.1rem;
        text-align: center;
      }
      .notif-dropdown {
        position: absolute;
        top: -0.35rem;
        right: 0;
        transform: translateY(-100%);
        background: var(--board);
        border: 1px solid var(--outline);
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
        border-radius: 0.5rem;
        padding: 0.5rem;
        min-width: 14rem;
        z-index: 10000;
        display: flex;
        flex-direction: column;
        gap: 0.25rem;
        .notif-item {
          font-size: 0.85rem;
          color: var(--text-primary);
          line-height: 1.4;
          padding: 0.35rem 0.4rem;
          border-radius: 0.35rem;
          background: var(--side-sub);
          cursor: pointer;
          &:hover {
            background: var(--tonal);
          }
        }
        .empty {
          color: var(--text-secondary);
          text-align: center;
          padding: 0.35rem 0.4rem;
          font-size: 0.85rem;
        }
      }
      &.folded {
        .notif-dropdown {
          position: fixed;
          right: auto;
          left: 0;
          top: auto;
        }
      }
    }
    &.folded {
      .user-info {
        order: 2;
      }
      .notification-wrapper {
        order: 1;
      }
    }
  }
  .main-select {
    > .flex-between {
      > div {
        > ul {
          position: static;
        }
      }
    }
  }
  .main-list,
  .alarm-stop-list,
  .main-select {
    display: flex;
    position: relative;
    height: 3rem;
    padding: 0 0.25rem;
    align-items: center;
    gap: 0.25rem;
    user-select: none;
    cursor: pointer;
    border-radius: 0.25rem;
    width: 100%;
    box-sizing: border-box;
    min-width: 0;
    .locationLabel {
      font-weight: 600;
      color: var(--text-tertiary);
      /* background-color: var(--tonal); */
      // padding: 0 0.75rem;
      height: 2rem;
      border-radius: 1rem;
      font-size: 0.875rem;
      gap: 0.5rem;
      span.bullet {
        background-color: var(--tonal-deep);
        border-radius: 1rem;
        padding: 0.25rem;
      }
      > div {
        flex-grow: 1;
        white-space: nowrap;
        text-overflow: ellipsis;
      }
    }
    .main-title {
      color: var(--text-primary);
      font-weight: var(--font-semibold);
      line-height: 1.4;
      /* font-weight: var(--font-medium); */
    }
    .icon-menu {
      font-size: 1.5rem;
      display: flex;
      justify-content: center;
      align-items: center;
      height: 3rem;
      width: 3rem;
      color: var(--text-tertiary);
      &.padding {
        width: 3.5rem;
        padding: 0 0.5rem;
      }
      > span {
        font-variation-settings: 'FILL' 1;
      }
    }
    .arrow-icon {
      font-size: 1.25rem;
      display: flex;
      justify-content: center;
      align-items: center;
      margin-left: auto;
      color: var(--text-tertiary);
      transition: transform 0.3s ease;
      &.open {
        transform: rotate(180deg);
      }
    }
  }

  .main-select {
    width: 100%;
    padding: 0px 0.75rem;
    margin: 0.5rem 0px;
    cursor: default;
    flex-direction: column;
    height: fit-content;
    .textLabel {
      text-align: center;
      cursor: pointer;
      height: 2.5rem;
      border-radius: 5px;
      line-height: 2.5rem;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;

      &:hover {
        background-color: rgb(209 213 219 / 0.5);
      }
    }

    > div {
      width: inherit;
      > div {
        flex: 0.5;
        overflow: hidden;
        &:nth-child(2) {
        }
        ul,
        li {
          width: inherit !important;
          justify-content: space-between;
          > span {
            font-size: 0.825rem !important;
          }
          > ul > li > span {
            font-size: 0.825rem;
          }
        }
      }
    }

    .full-width {
      > div > ul {
        position: static;
        > li {
          max-width: 100%;
        }
      }
    }

    .side-toggle {
      .icon-menu {
        font-size: 0.8rem;
      }
      .toggle-text {
        flex: 1;
        gap: 0.5rem;
        color: var(--text-tertiary);
        padding: 0.5rem 0;
        text-align: end;
        font-size: 0.8rem;
        line-height: 1.4;
        white-space: nowrap;
        :hover {
          cursor: pointer;
          color: var(--filled-orange);
        }
      }
    }
  }

  @media (min-width: 1025px) {
    .main-list:hover {
      background-color: rgb(209 213 219 / 0.5);
      * {
        color: rgb(0 0 0 / 0.8);
        color: var(--text-primary);
      }
      .icon-menu > span {
        font-variation-settings: 'FILL' 1;
      }
      .menu-tooltip {
        opacity: 1;
        color: white;
        visibility: visible;
        &.bottom {
          top: -1.75rem;
        }
      }
    }
  }
  .main-list.active {
    background-color: var(--selected-primary);
    .icon-menu > span {
      font-variation-settings: 'FILL' 1;
    }
    * {
      color: #ffffff;
    }
  }
  .main-list.focus {
    background-color: rgb(209 213 219);
    .icon-menu > span {
      font-variation-settings: 'FILL' 1;
    }
    * {
      color: var(--gray-900);
    }
  }
  .menu-tooltip {
    visibility: visible;
    font-size: 0.75rem;
    color: white;
    background-color: black;
    font-weight: 300;
    opacity: 0;
    border-radius: 0.75rem;
    position: absolute;
    left: 50%;
    transform: translateX(-50%);
    top: 3rem;
    z-index: 6000;
    text-align: center;
    width: max-content;
    transition: ease-in-out;
    transition-duration: 100ms;
    transition-delay: 700ms;
    padding: 0.125rem 0.5rem;
    user-select: none;
    pointer-events: none;
    &.bottom {
      top: -1.75rem;
    }
  }

  .tunnelChip {
    width: 100%;

    > div {
      flex-grow: 1 !important;
      width: inherit;
      height: 2rem;
      border-radius: 1rem;
      padding: 0.25rem 0.75rem;
      border: 1px solid var(--outline);
      background-color: var(--tonal-blue);
      color: var(--filled-blue-deep);
      margin-top: 0.5rem;
      font-size: 0.875rem;
      cursor: pointer;
      &:hover {
        background-color: #ffffff;
        box-shadow: 0 4px 12px 0 rgba(0, 0, 0, 0.1);
      }

      > div {
        display: flex;
        gap: 0.5rem;
        font-weight: 600;

        .material-symbols-rounded {
          font-size: 1.5rem;
          color: var(--filled-blue-deep);
        }
      }

      .material-symbols-rounded {
        font-variation-settings: 'FILL' 1, 'wght' 400, 'GRAD' 0, 'opsz' 24;
        color: var(--filled-blue-deep);
      }
    }
  }

  .alarm-stop {
    width: 100%;

    > div {
      display: flex;
      align-items: center;
      flex-grow: 1 !important;
      width: inherit;
      border-radius: 2rem;
      padding: 0.5rem 0.75rem;
      border: 1px solid var(--filled-red-deep);
      background-color: var(--tonal-red);
      color: var(--filled-red-deep);
      margin-top: 0.5rem;
      font-size: 0.875rem;
      line-height: 1.2;
      cursor: pointer;
      gap: 0.5rem;
      font-weight: 600;

      .material-symbols-rounded {
        font-size: 1.5rem;
        font-variation-settings: 'FILL' 1, 'wght' 400, 'GRAD' 0, 'opsz' 24;
      }
    }
  }
`;

const mobile = isMobile();

// 승인 요청 쿼리 설정 생성 함수
const createApprovalQueries = (hCd: string, userId: string, openAside: boolean) => {
  const req = [
    // { formType: 'creport', approvalType: 'approval', path: '/creport/received', formName: '보고서', url: '/' },
    { formType: 'cdraft', approvalType: 'approval', path: '/cdraft/history', formName: '기안서', url: '/c_work4', mtCd: 'C001', mdCd: '004', mrCd: '002', statusKey: 'status' },
    { formType: 'attendance', approvalType: 'approval', path: '/attendance/history', formName: '근태문서', url: '/c_work5', mtCd: 'C001', mdCd: '005', mrCd: '002', statusKey: 'caStatus' },
  ];

  return {
    queries: req.map((item) => ({
      queryKey: [`approval_${item.formType}_${item.approvalType}`, hCd, openAside],
      queryFn: async () => {
        const res = await apiGet({ path: item.path, req: { cCd: hCd } });
        const responseData = res?.data?.data || [];
        const matchCount = Array.isArray(responseData) ? responseData.filter((el: any) => el.currentUserId === userId && Number(el[item.statusKey]) < 3).length : 0;
        return {
          hasAlert: matchCount > 0,
          formType: item.formType,
          count: matchCount,
          formName: item.formName,
          url: item.url,
          mtCd: item.mtCd,
          mdCd: item.mdCd,
          mrCd: item.mrCd,
        };
      },
      enabled: !!hCd && hCd?.substring(0, 1)?.toLowerCase() === 'c' && (mobile ? openAside : true),
      refetchOnWindowFocus: !mobile,
    })),
  };
};

const AsideMainMenu = ({
  foldMenu,
  foldingMenu,
  sideMenuList,
  onClickAsideList,
  mainSelected,
  mainFocus,
  closeAside,
  openMenu,
  useBroadcast,
  W,
  hasVisitorMenuAuth,
  subMenu,
  pageMovement,
  setLinkPath,
  subSelected,
  setSubSelected,
  setMainFocus,
}: AsideMainMenuProps) => {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const [theme, setTheme] = useRecoilState(themeState);
  const [userInfo, setUserInfo] = useRecoilState(userState);
  const pageInfo = useRecoilValue(pageInfoState);

  useLayoutEffect(() => {
    if (subMenu && subMenu.length > 0) {
      subMenu.map((el: any) => {
        el.sub2?.map((el2: any) => {
          if (el2.mdCd === pageInfo.mdCd) {
            setSubSelected(el2.mdCd);
          }
        });
      });
    }
  }, [subMenu, pageInfo.mdCd, setSubSelected]);
  const [openModal, setOpenModal] = useState<any>({ status: false, type: 'userSetting', title: t('사용자 정보') });
  const [headList, setHeadList] = useState<any[]>([]);
  const [siteList, setSiteList] = useState<any[]>([]);
  const [companyList, setCompanyList] = useState<{ type: string; cCd: string; cdName: string }[] | null>(null);
  const [hCd, setHcd] = useState({ type: 'hCd', hCd: userInfo.hCd !== 'M000' ? userInfo.hCd : '', cdName: userInfo.hName });
  const [sCd, setScd] = useState({ type: 'sCd', sCd: userInfo.sCd, cdName: userInfo.sName });
  const [cCd, setcCd] = useState({ type: 'cCd', cCd: userInfo.hCd !== 'M000' ? userInfo.hCd : '', cdName: userInfo.hName });
  const [firstRenderCheck, setFirstRenderCheck] = useState(false);
  const randomString = Math.random().toString(36).substring(7); // 랜덤 문자열을 쿼리스트링으로 추가해서 이미지 캐싱 방지
  const navigate = useNavigate();
  const setMsiteUseYn = useSetRecoilState(msiteUseYnState);
  const setMsiteList = useSetRecoilState(msiteListState);
  const setPageInfo = useSetRecoilState<IPageInfo>(pageInfoState);
  const { data: companies } = useQuery(['/company/info', userInfo.hCd], () => apiGet({ path: '/company/info' }));
  const [isHeadSelect, setIsHeadSelect] = useState(userInfo.hCd === '' || userInfo.hCd === 'M000' || userInfo.hCd?.substring(0, 1).toLowerCase() === 'h');
  const openAside = useRecoilValue(asideState);

  useEffect(() => {
    if (companies) {
      const newArray = companies.data.data.map((el: any) => ({ type: 'cCd', cCd: el.cCd, cdName: el.cName }));
      setCompanyList(newArray);
    }
  }, [companies]);

  useEffect(() => {
    setHcd({ type: 'hCd', hCd: userInfo.hCd !== 'M000' ? userInfo.hCd : '', cdName: userInfo.hName });
    setScd({ type: 'sCd', sCd: userInfo.sCd, cdName: userInfo.sName });
    setcCd({ type: 'cCd', cCd: userInfo.hCd !== 'M000' ? userInfo.hCd : '', cdName: userInfo.hName });
  }, [userInfo]);

  useEffect(() => {
    if (userInfo.hCd !== hCd.hCd && pathname !== '/') {
      setUserInfo((prev) => ({ ...prev, hCd: hCd.hCd, hName: hCd.cdName, isCompany: false }));
      navigate('/');
    }
  }, [hCd.hCd]);

  useEffect(() => {
    if (userInfo.sCd !== sCd.sCd && pathname !== '/') {
      setUserInfo((prev) => ({ ...prev, sCd: sCd.sCd, sName: sCd.cdName, isCompany: false }));
      navigateHome() && navigate('/');
    }
  }, [sCd.sCd]);

  useEffect(() => {
    if (userInfo.isCompany && userInfo.hCd !== cCd.cCd) {
      setUserInfo((prev) => ({ ...prev, hCd: cCd.cCd, hName: cCd.cdName, sCd: '00000', sName: '', isCompany: true }));
      navigate('/');
    }
  }, [cCd.cCd]);

  // 현장선택 변경 시 홈으로 이동하는 조건 함수
  const navigateHome = () => {
    if (pathname === '/h_visitor1') return false;
    if (pathname === '/h_visitor2') return false;
    return true;
  };

  /*
  useEffect(() => {
    // 본사 또는 현장 상태에서 본사/사내 선택값 변경시 홈으로 이동
    if (userInfo.gCd === 'MM' && pathname !== '/' && userInfo.hCd?.substring(0, 1)?.toLowerCase() !== 'c') navigate('/');
  }, [isHeadSelect]);
  */

  useEffect(() => {
    // 본사 또는 현장 상태에서 본사/사내 선택값 변경시 홈으로 이동
    if (userInfo.gCd === 'MM' && pathname !== '/' && userInfo.hCd?.substring(0, 1)?.toLocaleLowerCase() !== 'c') navigate('/');
  }, [userInfo.isCompany]);

  // msiteUseYn, msiteList 전역변수 저장
  useEffect(() => {
    if (hCd.hCd !== '' && sCd.sCd !== '') {
      Promise.all([
        apiGet({ path: '/site' }), //
        getMSiteList('/site/msite', { hCd: hCd.hCd, sCd: sCd.sCd }),
      ]).then(([siteRes, msiteRes]) => {
        const { statusCode, data } = siteRes.data;
        if (statusCode === 200) {
          const filteredSite = data.siteInfoList.filter(
            (el: any) =>
              el.delYn === 'N' && //
              el.hCd === hCd.hCd &&
              el.sCd === sCd.sCd
          );

          const msiteUseYn =
            filteredSite.length > 0 //
              ? filteredSite[0].msiteYn
              : 'N';

          setMsiteUseYn(msiteUseYn);
          setMsiteList(msiteRes);
        }
      });
    }
  }, [hCd.hCd, sCd.sCd]);

  const darkModeHandling = () => {
    if (theme === 'light') setTheme('dark');
    else setTheme('light');
    if (cctv.getPluginOBJECT()?.oPlugin && pathname === '/') {
      cctv.destroy();
    }
  };

  const headInfoReq = userInfo.hCd.substring(0, 1).toLowerCase() === 'h' ? { path: '/head/info', req: { hCd: userInfo.hCd } } : { path: '/company/info/one', req: { cCd: userInfo.hCd } };
  const headInfoQuery = useQuery([`headInfoGet`, userInfo.hCd], () => apiGet(headInfoReq), { retry: 3 });
  const headListQuery = useQuery(['headListGet'], () => apiGet({ path: '/menu/headInfo' }));
  const siteListQuery = useQuery(['siteListGet', hCd.hCd], () => apiGet({ path: '/menu/siteInfo', req: { hCd: hCd.hCd } }), { enabled: !!hCd.hCd });
  const logo = useThemeLogo(headInfoQuery, theme, userInfo.hCd.substring(0, 1).toLowerCase());

  const [tableState2, setTableState2] = useState<any[]>([]);
  const [openNotification, setOpenNotification] = useState(false);
  const notificationButtonRef = useRef<HTMLButtonElement>(null);
  const notificationWrapperRef = useRef<HTMLDivElement>(null);
  const [dropdownPosition, setDropdownPosition] = useState<React.CSSProperties>({});
  const menuItemRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});
  const [flyoutPosition, setFlyoutPosition] = useState<{ [key: string]: { top: number; left: number; maxHeight: number } }>({});
  const bsQuery = useQuery(['bs', userInfo], () => apiGet({ path: '/bs', req: { hCd: userInfo.hCd, sCd: userInfo.sCd } }), {
    enabled: !!userInfo.hCd && !!userInfo.sCd && userInfo.sCd !== '00000' && useBroadcast.isActiveMenu,
  });

  useEffect(() => {
    if (bsQuery.isSuccess && bsQuery.data.status === 200) {
      const { data } = bsQuery.data.data;
      setTableState2(data.filter((el: any) => el.useYn === 'Y'));
    }
  }, [bsQuery.isSuccess, bsQuery.isRefetching]);

  useEffect(() => {
    if (headListQuery.isSuccess && headListQuery.data && headListQuery.data.status === 200) {
      const newArray = headListQuery.data.data.data.map((el: any) => ({ type: 'hCd', hCd: el.hCd, cdName: el.hName }));
      setHeadList(newArray);
    }
  }, [headListQuery.isSuccess, headListQuery.isRefetching]);

  useEffect(() => {
    if (foldMenu && openNotification && notificationButtonRef.current) {
      const buttonRect = notificationButtonRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: `${buttonRect.top - 0.35}px`,
        left: `${buttonRect.left}px`,
        right: 'auto',
        transform: 'translateY(-100%)',
      } as React.CSSProperties);
    } else if (!openNotification) {
      setDropdownPosition({});
    }
  }, [foldMenu, openNotification]);

  useEffect(() => {
    if (siteListQuery.isSuccess && siteListQuery.data && siteListQuery.data.status === 200) {
      const newArray = siteListQuery.data.data.data.map((el: any) => ({ type: 'sCd', sCd: el.sCd, cdName: el.sName }));
      setSiteList(newArray);
    }
  }, [siteListQuery.isSuccess, siteListQuery.isRefetching]);

  useEffect(() => {
    if (cctv.getPluginOBJECT()?.oPlugin) {
      if (openModal.status) {
        cctv.hideWndAll();
      } else {
        cctv.showWndAll();
      }
    }
  }, [openModal.status]);

  useEffect(() => {
    // 영업담당자의 경우 본사메뉴 노출
    if (hCd.hCd && (userInfo.gCd === 'MM' || userInfo.gCd === 'AM') && firstRenderCheck) {
      // 본사만 선택했을 때
      const findHName = headList.find((el) => el.hCd === hCd.hCd);
      setUserInfo((prev) => ({
        ...prev,
        hCd: hCd.hCd,
        hName: findHName ? findHName.cdName : prev.hName,
        sCd: '00000',
        sName: '',
      }));
      setScd({ type: 'sCd', sCd: '', cdName: '' });
    } else {
      setFirstRenderCheck(true);
    }
  }, [hCd.hCd]);

  useEffect(() => {
    if (hCd.hCd && sCd.sCd && (userInfo.gCd === 'MM' || userInfo.gCd === 'HM' || userInfo.gCd === 'AM')) {
      /**
       * 수정자 : 홍선영
       * 날짜 : 2023.12.01
       * 수정내용 : 사이드바 로고를 클릭하여 대시보드 리로드시, 전역상태값인 setUserInfo가 여러번 set되어서 페이지가 여러번 리로드되는 문제 수정.
       * getSiteInfoAPI에서 setUserInfo 하고 있으므로, 아래의 중복되는 setUserInfo 주석처리함.
       *
       * 수정자 : 한영광
       * 수정일자 : 2023.12.05
       * 수정내용 : 현장 정보 조회, 사이드 메뉴 조회 API 요청은 이제 Aside 컴포넌트에서만 하도록 수정 (userInfo.hCd, userInfo.sCd 상태값이 변경되면 API 요청함)
       */
      const findSName = siteList.find((el) => el.sCd === sCd.sCd);
      setUserInfo((prev) => ({
        ...prev,
        sCd: sCd.sCd,
        sName: findSName ? findSName.cdName : prev.sName,
      }));
      // getSiteInfoAPI(hCd.hCd, sCd.sCd);
      // if (userInfo.sCd !== sCd.sCd) getMenuSiteInfoAPI(hCd.hCd, sCd.sCd);
    }
  }, [sCd.sCd]);

  const findMenuIcon = (mtName: string) => {
    switch (mtName) {
      case '방송하기':
        return 'campaign';
      case '알람중지':
        return 'alarm_off';
      case '마스터 관리':
        return 'key';
      case '마스터 메뉴 관리':
        return 'tune';
      case '일반 관리':
        return 'settings';
      case '사내메뉴타이틀1':
        return 'title';
      case '사내메뉴타이틀2':
        return 'subtitles';
      case '사내메뉴타이틀3':
        return 'list';
      case '본사 소개':
        return 'apartment';
      case '사용자 관리':
        return 'manage_accounts';
      case '현장 정보 / CCTV':
        return 'videocam';
      case '현장 소개':
        return 'pin_drop';
      case '근로자 관리':
        return 'groups';
      case '출역 관리':
        return 'calendar_month';
      case '안전 관리':
        return 'heart_check';
      case '센서 관리':
        return 'sensors';
      case '현장 CCTV':
        return 'videocam';
      case '설정 관리':
        return 'settings';
      case '터널 관리':
        return 'database';
      case '방송 장비 관리':
        return 'google_home_devices';
      case '태블릿 근로자 관리':
        return 'tablet';
      case 'LLM':
        return 'psychology';
      case '업무 관리':
        return 'task';
      case '사내 관리':
        return 'domain ';
      case '사원 관리':
        return 'manage_accounts';
      case '자유게시판':
        return 'forum';
      case '방문자 현황 관리':
        return 'badge';
      case '위치 관리':
        return 'location_on';
      default:
        return 'fiber_manual_record';
    }
  };

  // 사용자설정 클릭
  const onClickOpenUserSetting = () => {
    setOpenModal((prev: any) => ({ ...prev, status: true, type: 'userSetting' }));
  };

  const onClickClose = () => {
    setOpenModal((prev: any) => ({ ...prev, status: false, type: 'userSetting' }));
    // 데스크탑에서는 사이드바를 닫지 않음 (모바일에서만 닫기)
    if (W <= 1024) {
      closeAside();
    }
  };

  // 알람 중지
  const onClickAlarmStop = async () => {
    try {
      await axios.get(`${process.env.REACT_APP_BASE_URL}/bs/alarmstop?hCd=${userInfo.hCd}&sCd=${userInfo.sCd}`);
      await axios.get(`${process.env.REACT_APP_BASE_URL}/bs/schedulestop?hCd=${userInfo.hCd}&sCd=${userInfo.sCd}`);
      toast.success(t('알람 중지 성공'));
      await logPost({
        hCd: userInfo.hCd,
        sCd: userInfo.sCd,
        userId: userInfo.userId,
        menu: '알람중지',
        action: '알람중지',
        etc: `현장 : ${userInfo.sName}`,
      });
    } catch (error) {
      toast.error(t('알람 중지 실패'));
    }
  };

  const onClickHName = () => {
    onClickAsideList('home');
    // 본사명 라벨 클릭시 현장선택 초기화
    setUserInfo((prev) => ({
      ...prev,
      hCd: userInfo.hCd,
      sCd: '00000',
      sName: '',
    }));

    setScd({ type: 'sCd', sCd: '', cdName: '' });
  };

  const renderHcdScd = () => {
    if (userInfo.gCd === 'MM') {
      const loading = !companyList;
      if (loading) return null;

      if (!userInfo.isCompany) {
        return (
          <div className='flex-between'>
            <SearchSelectBoxSm
              options={headList}
              defaultOption={userInfo.hCd?.substring(0, 1) === 'H' ? userInfo.hName : t('본사 선택')}
              state={hCd}
              setState={setHcd}
              stateKey='hCd'
              codeKey='cdName'
              initiateKey={hCd.hCd}
              asideMain
            />
            <SearchSelectBoxSm
              options={siteList}
              defaultOption={userInfo.sName ? userInfo.sName : t('현장 선택')}
              state={sCd}
              setState={setScd}
              stateKey='sCd'
              codeKey='cdName'
              initiateKey={sCd.sCd}
              asideMain
            />
          </div>
        );
      }
      return (
        <div className='full-width'>
          <SearchSelectBoxSm
            options={companyList}
            defaultOption={userInfo.hCd?.substring(0, 1) === 'C' ? userInfo.hName : t('회사 선택')}
            state={cCd}
            setState={setcCd}
            stateKey='cCd'
            codeKey='cdName'
            initiateKey={cCd.cCd}
            asideMain
          />
        </div>
      );
    }

    if ((hasVisitorMenuAuth && userInfo.gCd === 'HM') || userInfo.gCd === 'AM') {
      return (
        <div className='flex-between locationLabel'>
          <div className='textLabel' role='presentation' onClick={onClickHName}>
            {userInfo.hName}
          </div>
          <SearchSelectBoxSm
            options={siteList}
            defaultOption={userInfo.sName ? userInfo.sName : t('현장 선택')}
            state={sCd}
            setState={setScd}
            stateKey='sCd'
            codeKey='cdName'
            initiateKey={sCd.sCd}
            asideMain
          />
        </div>
      );
    }

    if (userInfo.gCd === 'CM' || userInfo.gCd === 'CU') {
      return (
        <div className='flex-basic locationLabel'>
          <div>{userInfo.hName}</div>
        </div>
      );
    }

    return (
      <div className='flex-basic locationLabel'>
        {userInfo.gCd?.substring(0, 1) !== 'S' && <div className='text-center'>{userInfo.hName}</div>}
        <div>{userInfo.sName}</div>
      </div>
    );
  };

  const handleLogoClick = () => {
    // 본사 관리자가 현장선택한 상태에서 사이드바 로고 클릭시 현장선택 초기화
    if (userInfo.gCd === 'HM' && userInfo.sCd !== '00000') {
      setUserInfo((prev) => ({ ...prev, sCd: '00000', sName: '' }));
      if (pathname !== '/') onClickAsideList('home');
    } else if (pathname === '/') {
      window.location.reload();
    } else {
      onClickAsideList('home');
    }
  };

  const handleHeadSelect = () => {
    // setIsHeadSelect((prev) => !prev);
    setUserInfo((prev) => ({
      ...prev,
      // hCd: '',
      // hName: '',
      // sCd: '00000',
      // sName: '',
      isCompany: !prev.isCompany,
    }));
  };

  const handleClickBroadcasting = () => {
    if (tableState2.length > 0) {
      setOpenModal({ status: true, type: 'broadcast', title: t('실시간 방송'), data: tableState2, pageMoveFunction: useBroadcast.pageMoveFunction });
    } else toast.warning(t('방송장비 목록이 없습니다.'));
  };

  const approvalQueries = useQueries(createApprovalQueries(userInfo.hCd, userInfo.userId, openAside));
  const approvalAlertCount = approvalQueries.reduce((acc, query) => {
    const count = typeof query.data?.count === 'number' ? query.data.count : 0;
    return acc + count;
  }, 0);
  const hasApprovalAlert = approvalAlertCount > 0;
  const approvalAlertMessages = approvalQueries
    .map((query) => query.data)
    .filter((d: any) => d && typeof d.count === 'number' && d.count > 0)
    .map((d: any) => ({
      text: `결재하실 ${d.formName}가 ${d.count}건 있습니다.`,
      url: d.url || '/',
      mtCd: d.mtCd,
      mdCd: d.mdCd,
      mrCd: d.mrCd,
    }));

  const toggleNotification = () => {
    if (foldMenu && !openNotification && notificationButtonRef.current) {
      const rect = notificationButtonRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: `${rect.top - 0.35}px`,
        left: `${rect.left}px`,
        right: 'auto',
        transform: 'translateY(-100%)',
      } as React.CSSProperties);
    }
    if (!foldMenu) {
      setDropdownPosition({});
    }
    setOpenNotification((prev) => !prev);
  };

  // 알림 드롭다운 바깥 클릭 시 닫기
  useEffect(() => {
    if (!openNotification) return undefined;
    const handleClickOutside = (e: MouseEvent) => {
      if (notificationWrapperRef.current && !notificationWrapperRef.current.contains(e.target as Node)) {
        setOpenNotification(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [openNotification]);

  // 플라이아웃 메뉴 위치 업데이트
  useEffect(() => {
    if (foldMenu && mainFocus) {
      const menuItem = menuItemRefs.current[mainFocus];
      if (menuItem) {
        const updatePosition = () => {
          const rect = menuItem.getBoundingClientRect();
          const viewportHeight = window.innerHeight;
          // 화면 하단까지의 거리 계산 (여백 1rem = 16px)
          const maxHeight = viewportHeight - rect.top - 16;
          setFlyoutPosition((prev) => ({
            ...prev,
            [mainFocus]: {
              top: rect.top,
              left: rect.right + 0.5 * 16, // margin-left: 0.5rem
              maxHeight,
            },
          }));
        };
        updatePosition();
        window.addEventListener('scroll', updatePosition, true);
        window.addEventListener('resize', updatePosition);
        return () => {
          window.removeEventListener('scroll', updatePosition, true);
          window.removeEventListener('resize', updatePosition);
        };
      }
    }
    return undefined;
  }, [foldMenu, mainFocus]);

  return (
    <Root className={openMenu ? 'showRoot' : 'hideRoot'}>
      <div className={`${!foldMenu ? 'aside-default' : 'aside-fold'} aside`} id='aside'>
        {/* <div className='aside-top'>
          <div className='icon-menu hamburger' role='presentation' onMouseDown={(e) => foldingMenu(e)}>
            <span className={`${foldMenu && 'rotate180'} material-symbols-rounded`}>menu_open</span>
          </div>
          <div className='icon-menu close-menu' role='presentation' onMouseDown={() => closeAside()}>
            <span className='material-symbols-rounded'>close </span>
          </div>
          {!foldMenu && logo && (
            <a role='presentation' onClick={handleLogoClick} className='logo'>
              <img src={`${logo}?v=${randomString}`} alt='' />
            </a>
          )}
        </div> */}
        <div className='aside-header'>
          <div className='icon-menu close-menu' role='presentation' onMouseDown={() => closeAside()}>
            <span className='material-symbols-rounded'>close</span>
          </div>
          <div className='aside-info'>
            {!foldMenu ? (
              <>
                <a role='presentation' onClick={handleLogoClick} className='logo'>
                  {logo && (
                    <figure>
                      <img src={`${logo}?v=${randomString}`} alt='' />
                    </figure>
                  )}
                  <h1 title={userInfo.hName || ''}>{userInfo.hName || ''}</h1>
                </a>

                <div className='side-right'>
                  {userInfo.gCd === 'MM' && (
                    <div className='toggle-text side-right-icon icon-select' title={!userInfo.isCompany ? t('회사 선택') : t('본사 선택')} role='button' tabIndex={0} onClick={handleHeadSelect}>
                      <span className='material-symbols-rounded'>cached</span>
                    </div>
                  )}
                  <div className='side-right-icon icon-open' role='presentation' onMouseDown={(e) => foldingMenu(e)} title={t('사이드바 닫기')}>
                    <span className='material-symbols-rounded'>chevron_left</span>
                  </div>
                </div>
              </>
            ) : (
              <div className='icon-close' role='presentation' onMouseDown={(e) => foldingMenu(e)} title={t('사이드바 열기')}>
                <span className='material-symbols-rounded'>chevron_right</span>
              </div>
            )}
          </div>
        </div>
        {!foldMenu && (
          <div className='main-select' role='presentation'>
            {foldMenu && (
              <div className='icon-menu padding'>
                <span className='material-symbols-rounded'>published_with_changes</span>
              </div>
            )}
            {foldMenu && <span className='menu-tooltip'>{t('본사/현장선택')}</span>}
            {renderHcdScd()}
            {/* {userInfo.gCd === 'MM' && (
              <div className='side-toggle flex-end' role='button' tabIndex={0} onClick={handleHeadSelect}>
                <div className='toggle-text flex-end'>
                  <span className='material-symbols-rounded'>login</span>
                  {!userInfo.isCompany ? t('회사 선택') : t('본사 선택')}
                </div>
              </div>
            )} */}

            {!foldMenu && useBroadcast.isActiveMenu && (
              <div className='tunnelChip flex-center'>
                <div className='flex-between' role='presentation' onClick={handleClickBroadcasting}>
                  <div>
                    <span className='material-symbols-rounded'>campaign</span>
                    {t('방송하기')}
                  </div>
                  <span className='material-symbols-rounded'>arrow_circle_right</span>
                </div>
              </div>
            )}
          </div>
        )}

        {useBroadcast.isActiveMenu && foldMenu && (
          <div className='aside-container fixed'>
            <a role='presentation' className='main-list' onClick={handleClickBroadcasting}>
              <div className='icon-menu'>
                <span className='material-symbols-rounded'>{findMenuIcon('방송하기')}</span>
              </div>
              <span className='menu-tooltip'>{t('방송하기')}</span>
              {/* {!foldMenu && <div className='main-title'>{t('방송하기')}</div>} */}
            </a>
          </div>
        )}

        <div className={`aside-container ${foldMenu ? 'fold' : ''}`}>
          {sideMenuList && sideMenuList?.length > 0
            ? sideMenuList?.map((menuEl) => {
                const isOpen = mainFocus === menuEl.mtCd;
                const currentSubMenu = subMenu && subMenu.length > 0 && subMenu[0]?.mtCd === menuEl.mtCd ? subMenu : [];
                const hasSubMenu = menuEl.sub && menuEl.sub.length > 1;
                return (
                  <div
                    key={menuEl.mtCd}
                    className='menu-item-wrapper'
                    ref={(el) => {
                      menuItemRefs.current[menuEl.mtCd] = el;
                    }}
                  >
                    <a
                      role='presentation'
                      onClick={(e) => {
                        if (foldMenu && hasSubMenu) {
                          // 접힘 + 하위메뉴가 있는 경우: 플라이아웃 토글만 하고 페이지 이동은 하지 않음
                          if (mainFocus === menuEl.mtCd) {
                            setMainFocus('');
                          } else {
                            setMainFocus(menuEl.mtCd);
                            // 메뉴 아이템의 위치를 기준으로 플라이아웃 메뉴 위치 계산
                            const menuItem = menuItemRefs.current[menuEl.mtCd];
                            if (menuItem) {
                              const rect = menuItem.getBoundingClientRect();
                              const viewportHeight = window.innerHeight;
                              const maxHeight = viewportHeight - rect.top - 16;
                              setFlyoutPosition((prev) => ({
                                ...prev,
                                [menuEl.mtCd]: {
                                  top: rect.top,
                                  left: rect.right + 0.5 * 16, // margin-left: 0.5rem
                                  maxHeight,
                                },
                              }));
                            }
                          }
                          return;
                        }
                        onClickAsideList(menuEl.sub);
                      }}
                      className={menuEl.mtCd === mainSelected ? 'active main-list' : menuEl.mtCd === mainFocus ? 'focus main-list' : 'main-list'}
                    >
                      <div className='icon-menu'>
                        <span className='material-symbols-rounded'>{findMenuIcon(menuEl.mtName)}</span>
                      </div>
                      {foldMenu && <span className='menu-tooltip'>{t(menuEl.mtName)}</span>}
                      {!foldMenu && (
                        <div key={menuEl.mtCd} className='main-title' title={t(menuEl.mtName)}>
                          {menuEl.useYn === 'N' ? <span style={{ color: 'red' }}>{t(menuEl.mtName)}</span> : t(menuEl.mtName)}
                        </div>
                      )}
                      {!foldMenu && hasSubMenu && <span className={`material-symbols-rounded arrow-icon ${isOpen ? 'open' : ''}`}>keyboard_arrow_down</span>}
                    </a>
                    {!foldMenu && currentSubMenu && currentSubMenu.length > 1 && (
                      <div className={`submenu-accordion ${isOpen ? 'open' : ''}`}>
                        <ul className='submenu-list'>
                          {currentSubMenu.map((sub: any) => (
                            <Link
                              key={sub.mdCd}
                              to={`${setLinkPath(sub.mtCd, sub.mdCd)}?tabCd=${sub.sub2[0]?.mrCd || '001'}`}
                              className={pageInfo.mtCd === sub.mtCd && sub.mdCd === subSelected ? 'active sub-list' : 'sub-list'}
                              onClick={() => pageMovement(sub)}
                            >
                              <li className='sub-title' title={t(sub.mdName)}>
                                {sub.useYn === 'N' ? <span style={{ color: 'red' }}>{t(sub.mdName)}</span> : t(sub.mdName)}
                              </li>
                            </Link>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* 접힘 상태에서 하위메뉴를 사이드바 밖에 플라이아웃으로 표시 */}
                    {foldMenu && hasSubMenu && mainFocus === menuEl.mtCd && (
                      <div
                        className='fold-submenu'
                        role='presentation'
                        style={
                          flyoutPosition[menuEl.mtCd]
                            ? {
                                top: `${flyoutPosition[menuEl.mtCd].top}px`,
                                left: `${flyoutPosition[menuEl.mtCd].left}px`,
                                maxHeight: `${flyoutPosition[menuEl.mtCd].maxHeight}px`,
                              }
                            : undefined
                        }
                      >
                        <div className='fold-submenu-title'>{t(menuEl.mtName)}</div>
                        <div className='fold-submenu-list'>
                          {menuEl.sub.map((sub: any) => (
                            <Link
                              key={sub.mdCd}
                              to={`${setLinkPath(sub.mtCd, sub.mdCd)}?tabCd=${sub.sub2[0]?.mrCd || '001'}`}
                              className={pageInfo.mtCd === sub.mtCd && sub.mdCd === subSelected ? 'active' : undefined}
                              onClick={() => {
                                pageMovement(sub);
                                setMainFocus('');
                              }}
                            >
                              {sub.useYn === 'N' ? <span style={{ color: 'red' }}>{t(sub.mdName)}</span> : t(sub.mdName)}
                            </Link>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })
            : undefined}
          <a role='presentation' className='main-list' onClick={darkModeHandling}>
            <div className='icon-menu'>
              <span className='material-symbols-rounded'>{theme === 'light' ? 'dark_mode' : 'light_mode'}</span>
            </div>
            {foldMenu && <span className='menu-tooltip'>{theme === 'light' ? t('다크모드 설정') : t('라이트모드 설정')}</span>}
            {!foldMenu && <div className='main-title'>{theme === 'light' ? t('다크모드 설정') : t('라이트모드 설정')}</div>}
          </a>
        </div>
        {/* 
            * 수정자 : 홍선영
            * 날짜 : 2023.10.17
            * 수정내용 : 사이드바에 본사명 선택기능 추가하여 아래 본사목록으로 돌아가는 기능 대체하였으므로 주석처리함.
            * 
          {(userInfo.gCd.substring(0, 1) === 'H' || userInfo.gCd.substring(0, 1) === 'A') && userInfo.sCd !== '00000' && (
            <div>
              <a role='presentation' className='main-list' onClick={headMenu}>
                <div className='icon-menu'>
                  <span className='material-symbols-rounded'>apartment</span>
                </div>
                {foldMenu && <span className='menu-tooltip'>본사 메뉴</span>}
                {!foldMenu && <div className='main-title'>본사 메뉴</div>}
              </a>
            </div>
          )} */}
        <div className={`aside-bottom ${foldMenu ? 'folded' : ''}`} style={{ flexDirection: foldMenu ? 'column' : 'row' }}>
          <div className='main-list user-info' role='presentation' onClick={onClickOpenUserSetting}>
            <div className='icon-menu'>
              <span className='material-symbols-rounded'>account_box</span>
            </div>
            {foldMenu && <span className='menu-tooltip bottom'>{t('사용자 정보')}</span>}
            {!foldMenu && <div className='main-title'>{t('사용자 정보')}</div>}
          </div>
          {/* <div className='language-select'>{!foldMenu && <Language />}</div> */}
          {userInfo.hCd?.substring(0, 1)?.toLowerCase() === 'c' && (
            <div ref={notificationWrapperRef} className={`notification-wrapper ${foldMenu ? 'folded' : ''}`}>
              <button ref={notificationButtonRef} className='main-list' type='button' onClick={toggleNotification}>
                <div className='icon-menu'>
                  <span className='material-symbols-rounded'>notifications</span>
                  {hasApprovalAlert && <span className='badge'>{approvalAlertCount}</span>}
                </div>
              </button>
              {openNotification && (
                <div className='notif-dropdown' style={foldMenu ? dropdownPosition : {}}>
                  {hasApprovalAlert ? (
                    approvalAlertMessages.map((msg: any, idx: number) => (
                      <div
                        key={`approval-${idx}`}
                        className='notif-item'
                        role='presentation'
                        onClick={() => {
                          navigate(`${msg.url}?tabCd=${msg.mrCd}`, { state: createApprovalAlertState() });
                          setPageInfo((prev: IPageInfo) => ({
                            ...prev,
                            defaultMrCd: msg.mrCd,
                            mtCd: msg.mtCd,
                            mdCd: msg.mdCd,
                            mrCd: msg.mrCd,
                          }));
                          setOpenNotification(false);
                          if (W <= 1024) {
                            closeAside();
                          }
                        }}
                      >
                        {msg.text}
                      </div>
                    ))
                  ) : (
                    <div className='empty'>{t('알림이 없습니다')}</div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      <Portal openModal={openModal?.status}>
        {openModal && openModal.type === 'userSetting' && <UserInfoSettingModal openModal={openModal} setOpenModal={setOpenModal} onClickClose={onClickClose} closeAside={closeAside} />}
        {openModal && openModal.type === 'broadcast' && <BroadcastLiveModal openModal={openModal} setOpenModal={setOpenModal} isTunnel={false} />}
      </Portal>
    </Root>
  );
};
export default AsideMainMenu;
